import React, { useContext } from 'react';
import type { ReactNode } from 'react';
import { InternalId, RuntimeDate } from '@edgebox/data-definition-kit';
import type { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { SyncCoreApiContext } from '../../../../contexts/SyncCoreApiContext';

export type AccessScopeFilter = import('@edgebox/sync-core-data-definitions').AccessScope | 'Any';
export type StatusFilter<T extends string> = T[] | 'Default';

export function useSyncCoreApi(): ISyncCoreApi | undefined {
  const apiContext = useContext(SyncCoreApiContext);
  return apiContext?.api;
}

export function normalizeFilterId(value: string | undefined): InternalId | undefined {
  const trimmed = value?.trim();
  return trimmed?.length ? (trimmed as InternalId) : undefined;
}

function runtimeDateToDate(value: RuntimeDate): Date | undefined {
  if (typeof value === 'string') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  if (value instanceof Date) return value;

  const maybeToDate = value as unknown as { toDate?: () => unknown };
  if (typeof maybeToDate.toDate === 'function') {
    const asDate = maybeToDate.toDate();
    if (asDate instanceof Date && !Number.isNaN(asDate.getTime())) return asDate;
  }

  return undefined;
}

export function formatDateTime(value?: RuntimeDate): string {
  if (!value) return '-';

  const date = runtimeDateToDate(value);
  if (date) return date.toLocaleString();

  const maybeToISOString = value as unknown as { toISOString?: () => string };
  if (typeof maybeToISOString.toISOString === 'function') return maybeToISOString.toISOString();

  return String(value);
}

export function formatMoney(value: number | undefined): string {
  if (value === undefined) return '-';
  if (!Number.isFinite(value)) return '-';
  return value.toLocaleString(undefined, { minimumFractionDigits: 6, maximumFractionDigits: 6 });
}

export function enumValues<T extends Record<string, string>>(enumObject: T): Array<T[keyof T]> {
  return Object.values(enumObject) as Array<T[keyof T]>;
}

function emptyValueNode(): ReactNode {
  return React.createElement('span', { className: 'text-muted' }, '-');
}

export function renderValue(value: unknown): ReactNode {
  if (value === undefined || value === null) return emptyValueNode();
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return Number.isFinite(value) ? value.toLocaleString() : String(value);
  if (typeof value === 'string') return value.length ? value : emptyValueNode();
  if (Array.isArray(value)) return value.length ? value.join(', ') : emptyValueNode();
  return React.createElement('pre', { className: 'mb-0', style: { whiteSpace: 'pre-wrap' } }, JSON.stringify(value, null, 2));
}
